import {
  archerConstants,
  LayoutGrid,
  makeStyles,
  Tooltip,
  Typography,
  _DeprecatedCheckbox as Checkbox,
  _DeprecatedCheckboxProps
} from '@archer-technologies/ui-components';
import { Table, TableHeaderRow, TableSelection, VirtualTable } from '@devexpress/dx-react-grid-material-ui';
import { TableProps } from '@mui/material/Table';
import * as React from 'react';
import { TableHeaderRowCellFilter, TableHeaderRowCellFilterProps } from './filtering/tableHeaderRowFilter.plugin';

interface TableHeaderRowCellComponentProps extends TableHeaderRow.CellProps {
  filterProps?: TableHeaderRowCellFilterProps;
}

const useStyles = makeStyles(() => {
  return {
    gridTable: {
      borderCollapse: 'collapse',
      '& .MuiTableRow-root': {
        '& .MuiTableCell-root': {
          '& > div:nth-child(1)': {
            '& > button:nth-child(1)': {
              margin: 0
            }
          }
        }
      }
    },
    tableTitleText: {
      display: 'initial'
    },
    checkboxCellStyles: {
      background: 'inherit',
      textAlign: 'center',
      verticalAlign: 'top',
      '& .MuiCheckbox-root': {
        '&:hover': { backgroundColor: archerConstants.archerButtonHover },
        '&:active': { backgroundColor: archerConstants.archerButtonHover }
      }
    },
    textOverflow: {
      overflow: 'hidden',
      textOverflow: 'ellipsis'
    },
    actionColumnStyles: {
      '& [class*="Content"]': {
        justifyContent: 'center',
        '& span': {
          display: 'flex',
          justifyContent: 'center',
          width: '100%',
          '& span[class*="Title"]': {
            display: 'flex',
            justifyContent: 'center'
          },
          '& svg': {
            display: 'none'
          }
        }
      }
    }
  };
});

const TableComponent = ({ children, ...restProps }: TableProps) => {
  // This component returns the Table element of the VirtualTable component inside the grid.
  // In this case, we are forcing the table layout to auto, so that it properly sizes within the
  // context of flex containers.
  const { gridTable } = useStyles();

  return (
    <VirtualTable.Table className={gridTable} style={{ tableLayout: 'auto' }} {...restProps}>
      {children}
    </VirtualTable.Table>
  );
};

const TableCellComponent = (props: Table.DataCellProps) => {
  // This component may be used to manipulate cells in the Table component.
  const { value, children } = props;
  const { textOverflow } = useStyles();
  const ref = React.useRef<HTMLDivElement>(null);

  const [isOverflown, setIsOverflown] = React.useState(false);
  React.useEffect(() => {
    if (ref.current) {
      setIsOverflown(ref.current.scrollWidth > ref.current.clientWidth);
    }
  });
  return (
    <Table.Cell {...props}>
      {children ||
        (typeof value === 'string' ? (
          <Typography variant="body1" color="textPrimary">
            <Tooltip title={value} disableHoverListener={!isOverflown}>
              <div className={textOverflow} ref={ref}>
                {value}
              </div>
            </Tooltip>
          </Typography>
        ) : (
          value
        ))}
    </Table.Cell>
  );
};

const wrapLayoutStyles = (children: any, isActionsColumn: boolean) => {
  const { actionColumnStyles } = useStyles();
  return isActionsColumn ? (
    <LayoutGrid className={isActionsColumn ? actionColumnStyles : ''} container wrap="nowrap">
      {children}
    </LayoutGrid>
  ) : (
    children
  );
};

const TableHeaderRowCellComponent = ({ children, filterProps, ...restProps }: TableHeaderRowCellComponentProps) => {
  // This component may be used to manipulate cells in the TableHeaderRow component.
  const isActionsColumn = restProps.column.name === 'actions';
  return (
    <TableHeaderRow.Cell {...restProps}>
      {wrapLayoutStyles(children, isActionsColumn)}
      {filterProps && <TableHeaderRowCellFilter {...filterProps} />}
      <div className="grid-table-header-row-cell-splitter" />
    </TableHeaderRow.Cell>
  );
};

const TableHeaderRowTitleComponent: React.FunctionComponent = ({ children }) => {
  // This component may be used to manipulate the titles in cells of the TableHeaderRow component.
  const { tableTitleText } = useStyles();
  const title = children?.toString() || '';

  const CustomTitle = React.forwardRef<HTMLDivElement, any>((undefined, ref) => (
    <div ref={ref}>
      <Typography variant="h6" color="textPrimary" className={tableTitleText} align="center">
        {title}
      </Typography>
    </div>
  ));

  return (
    <TableHeaderRow.Title>
      <Tooltip title={title}>
        <CustomTitle />
      </Tooltip>
    </TableHeaderRow.Title>
  );
};

const TableHeaderRowSortLabel = (props: TableHeaderRow.SortLabelProps) => (
  <TableHeaderRow.SortLabel {...props} className="table-header-row-sort-label" />
);

const TableHeaderRowGroupButtonComponent = (props: TableHeaderRow.GroupButtonProps) => {
  const { disabled } = props;
  let className = 'table-header-row-group-button';
  if (disabled) {
    className += ' table-header-row-group-button-disabled';
  }
  return <TableHeaderRow.GroupButton {...props} className={className} />;
};

const TableRowSelectCellComponent = ({ row, selected, onToggle }: TableSelection.CellProps) => {
  const { checkboxCellStyles } = useStyles();
  const items: _DeprecatedCheckboxProps['checkboxGroup']['items'] = [
    {
      description: `row-${row.id}-checkbox`,
      id: `row-${row.id}-checkbox`,
      isChecked: selected,
      isEnabled: row.disabledIndex === undefined ? true : row.disabledIndex
    }
  ];
  const checkboxProps: _DeprecatedCheckboxProps = {
    checkboxGroup: {
      items
    },
    onChange: () => onToggle,
    noFormControl: true
  };
  return (
    <td className={checkboxCellStyles}>
      <Checkbox {...checkboxProps} />
    </td>
  );
};

export {
  TableComponent,
  TableCellComponent,
  TableHeaderRowCellComponent,
  TableHeaderRowTitleComponent,
  TableHeaderRowSortLabel,
  TableHeaderRowGroupButtonComponent,
  TableRowSelectCellComponent
};
